import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const hrs = (v) => (v ? Number(v).toFixed(v % 1 ? 2 : 0) : "–");
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Tile = ({ label, value, testid, strong }) => (
  <div className={`rounded-2xl p-4 ${strong ? "bg-[#0B2D5B] text-white" : "border border-slate-200 bg-white"}`} data-testid={testid}>
    <p className={`text-xs font-bold uppercase tracking-wide ${strong ? "text-white/70" : "text-slate-500"}`}>{label}</p>
    <p className="mt-1 font-mono-num text-2xl font-bold">{value}</p>
  </div>
);

export const RosterCosts = ({ start, refreshKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/roster/costs", { params: { start } }).then((r) => setD(r.data)).catch(() => setD(false));
  }, [start, refreshKey]);
  if (d === false) return null;
  if (!d) return <div className="mb-5 h-40 animate-pulse rounded-2xl bg-white" data-testid="roster-costs-loading" />;
  const t = d.totals;
  return (
    <div className="mb-6 space-y-3" data-testid="roster-costs">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Weekly labour cost" value={money(t.labour_cost)} testid="roster-cost-total" strong />
        <Tile label="Wages" value={money(t.wages)} testid="roster-cost-wages" />
        <Tile label="Super" value={money(t.super)} testid="roster-cost-super" />
        <Tile label="Rostered hours" value={hrs(t.hours)} testid="roster-cost-hours" />
      </div>
      <p className="text-xs text-slate-500 lg:hidden">Swipe the table sideways to see every day, cost per hour and weekly cost.</p>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[760px] text-sm" data-testid="roster-cost-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left">Employee</th>
              <th className="px-2 py-2 text-right">Hrs</th>
              {DAYS.map((x, i) => <th key={x} className="px-2 py-2 text-center">{x}<span className="block text-[10px] font-normal normal-case">{d.days[i].slice(8)}/{d.days[i].slice(5, 7)}</span></th>)}
              <th className="px-2 py-2 text-right">Cost / hr</th>
              <th className="px-3 py-2 text-right">Weekly cost</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.membership_id} className="border-t border-slate-100" data-testid={`roster-cost-row-${r.membership_id}`}>
                <td className="sticky left-0 z-10 max-w-[160px] break-words bg-white px-3 py-2 font-semibold text-[#0B2D5B]">{r.name}</td>
                <td className="px-2 py-2 text-right font-mono-num font-bold">{hrs(r.total_hours)}</td>
                {r.days.map((h, i) => <td key={i} className={`px-2 py-2 text-center font-mono-num ${h ? "text-[#0B2D5B]" : "text-slate-300"}`}>{hrs(h)}</td>)}
                <td className="px-2 py-2 text-right font-mono-num">{r.hourly_rate != null ? money(r.hourly_rate) : r.wage_type ? "Fixed" : "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold">{money(r.weekly_cost)}</td>
              </tr>
            ))}
            {!d.rows.length && <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">No shifts rostered this week yet.</td></tr>}
          </tbody>
          {d.rows.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-bold">
              <tr>
                <td className="sticky left-0 z-10 bg-slate-50 px-3 py-2">Total</td>
                <td className="px-2 py-2 text-right font-mono-num" data-testid="roster-cost-foot-hours">{hrs(t.hours)}</td>
                {t.day_hours.map((h, i) => <td key={i} className="px-2 py-2 text-center font-mono-num">{hrs(h)}</td>)}
                <td />
                <td className="px-3 py-2 text-right font-mono-num" data-testid="roster-cost-foot-wages">{money(t.wages)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Predicted from this week's rostered shifts (drafts included), each person's pay settings, weekend and public holiday rates, and overtime. Fixed-wage staff show their weekly wage. Super is estimated at your super rate.
        {t.missing_rate.length > 0 && <span className="font-semibold text-[#B35F00]" data-testid="roster-cost-missing"> No pay rate set for: {t.missing_rate.join(", ")}.</span>}
      </p>
    </div>
  );
};
